/**
 * Tripnovac Visa — Checkout Success Page
 * Post-payment confirmation and next steps
 * 
 * @company Alpha Libra
 * @domain tripnovac-visa.com
 */

import React, { useEffect, useState } from 'react';
import {
  Box, Container, Paper, Typography, Button, Avatar, Grid,
  Card, CardContent, CircularProgress, List, ListItem,
  ListItemIcon, ListItemText, Chip,
} from '@mui/material';
import {
  CheckCircle, Dashboard, Assignment,
  Support, Bolt, Star, Receipt,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import confetti from 'canvas-confetti';
import { API_BASE_URL, STORAGE_KEYS } from '../../constants';

const CheckoutSuccessPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<any>(null);

  useEffect(() => {
    // Trigger confetti
    const duration = 3 * 1000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

    function randomInRange(min: number, max: number) {
      return Math.random() * (max - min) + min;
    }

    const interval: any = setInterval(function() {
      const timeLeft = animationEnd - Date.now();
      if (timeLeft <= 0) {
        return clearInterval(interval);
      }
      const particleCount = 50 * (timeLeft / duration);
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
      });
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
      });
    }, 250);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const verifyPayment = async () => {
      if (!sessionId) {
        setLoading(false);
        return;
      }

      try {
        // Fetch subscription status
        const res = await fetch(`${API_BASE_URL}/payments/subscription`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN)}` }
        });
        const data = await res.json();
        if (data.success) {
          setSubscription(data.data);
        }
      } catch (err) {
        console.error('Failed to verify payment');
      } finally {
        setLoading(false);
      }
    };

    // Small delay for effect
    setTimeout(verifyPayment, 1500);
  }, [sessionId]);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)'
        }}
      >
        <Paper sx={{ p: 6, borderRadius: 4, textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ mb: 3 }} />
          <Typography variant="h5" fontWeight={700}>
            Processing your payment...
          </Typography>
          <Typography color="text.secondary">
            Please wait while we confirm your subscription
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        py: 8,
        background: 'linear-gradient(180deg, #F0FDF4 0%, #ECFDF5 50%, #F8FAFC 100%)'
      }}
    >
      <Container maxWidth="md">
        {/* Success Header */}
        <Paper
          sx={{
            p: 6,
            borderRadius: 4,
            textAlign: 'center',
            mb: 4,
            background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
            color: 'white'
          }}
        >
          <Avatar
            sx={{
              width: 100,
              height: 100,
              bgcolor: 'rgba(255,255,255,0.2)',
              mx: 'auto',
              mb: 3
            }}
          >
            <CheckCircle sx={{ fontSize: 64 }} />
          </Avatar>
          
          <Typography variant="h3" fontWeight={800} gutterBottom>
            Welcome to Premium! 🎉
          </Typography>
          
          <Typography variant="h6" sx={{ opacity: 0.9, maxWidth: 500, mx: 'auto' }}>
            Your subscription is now active. You have access to all premium features.
          </Typography>

          {subscription?.tier && (
            <Chip
              icon={<Star />}
              label={`${subscription.tier.charAt(0).toUpperCase() + subscription.tier.slice(1)} Plan`}
              sx={{ mt: 3, bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 700 }}
            />
          )}
        </Paper>

        {/* What's Included */}
        <Paper sx={{ p: 4, borderRadius: 4, mb: 4 }}>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            🎁 What's Included in Your Plan
          </Typography>
          
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <List>
                <ListItem>
                  <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                  <ListItemText primary="Unlimited Visa Applications" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                  <ListItemText primary="Interview Preparation" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                  <ListItemText primary="AI Voice Coaching" />
                </ListItem>
              </List>
            </Grid>
            <Grid item xs={12} md={6}>
              <List>
                <ListItem>
                  <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                  <ListItemText primary="Document Checklists" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                  <ListItemText primary="Application Tracking" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                  <ListItemText primary="Priority Support" />
                </ListItem>
              </List>
            </Grid>
          </Grid>
        </Paper>

        {/* Next Steps */}
        <Typography variant="h5" fontWeight={700} gutterBottom>
          🚀 Next Steps
        </Typography>
        
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={4}>
            <Card 
              sx={{ 
                height: '100%', 
                cursor: 'pointer',
                transition: 'transform 0.2s',
                '&:hover': { transform: 'translateY(-4px)' }
              }}
              onClick={() => navigate('/dashboard')}
            >
              <CardContent sx={{ textAlign: 'center', p: 4 }}>
                <Avatar sx={{ bgcolor: 'primary.main', mx: 'auto', mb: 2, width: 56, height: 56 }}>
                  <Dashboard />
                </Avatar>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Go to Dashboard
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  View your account and track applications
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card 
              sx={{ 
                height: '100%', 
                cursor: 'pointer',
                transition: 'transform 0.2s',
                '&:hover': { transform: 'translateY(-4px)' }
              }}
              onClick={() => navigate('/applications/new')}
            >
              <CardContent sx={{ textAlign: 'center', p: 4 }}>
                <Avatar sx={{ bgcolor: 'success.main', mx: 'auto', mb: 2, width: 56, height: 56 }}>
                  <Assignment />
                </Avatar>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Start Application
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Create your first visa application now
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card 
              sx={{ 
                height: '100%', 
                cursor: 'pointer',
                transition: 'transform 0.2s',
                '&:hover': { transform: 'translateY(-4px)' }
              }}
              onClick={() => navigate('/countries')}
            >
              <CardContent sx={{ textAlign: 'center', p: 4 }}>
                <Avatar sx={{ bgcolor: 'warning.main', mx: 'auto', mb: 2, width: 56, height: 56 }}>
                  <Bolt />
                </Avatar>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Explore Countries
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Browse 249 countries and territories (ISO 3166-1)
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Receipt Info */}
        <Paper sx={{ p: 3, borderRadius: 3, bgcolor: 'grey.50' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Receipt color="action" />
            <Box>
              <Typography variant="body2" color="text.secondary">
                A receipt has been sent to your email address.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                You can manage your subscription anytime from your account settings.
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Support */}
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Need help getting started?
          </Typography>
          <Button
            variant="text"
            startIcon={<Support />}
            onClick={() => window.location.href = 'mailto:support@tripnovac.com'}
          >
            Contact Support
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default CheckoutSuccessPage;
